import { RefreshCw, Shuffle } from "lucide-react";

import CreatedCredentials from "./created-credentials";
import HolderSideFields from "./holder-side-fields";
import LoadingButton from "@components/ui-components/loading-button";
import SelectField from "@components/fields/select-field";
import useSuperadminHolders from "../hooks/use-superadmin-holders";

// Step 3: a brand's two logins, created together in the single array call the
// endpoint takes. The brand comes from the list in step 2 — picking it is what
// supplies api_key and account_ext_id, so neither is ever typed in.
const HoldersPanel = () => {
    const {
        brandOptions, selectedBrand, loading, methods, submit,
        created, dismissCreated, regenerateExtIds, refreshBrands
    } = useSuperadminHolders();

    const { control, watch, formState: { errors } } = methods;
    const createAdmin = watch("create_admin");
    const createAffiliate = watch("create_affiliate");

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">Brand logins</h2>
                    <p className="text-sm text-muted">
                        <code className="rounded bg-light px-1.5 py-0.5 text-xs text-gray-700">POST /api/onboarding/holder/v1</code>
                        {" — the brand admin and the publisher go up in one array."}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <LoadingButton color="neutral" size="sm" icon={RefreshCw} text="Reload brands" onClick={refreshBrands} />
                    <LoadingButton color="neutral" size="sm" icon={Shuffle} text="New external ids" onClick={regenerateExtIds} />
                </div>
            </div>

            <form
                className="space-y-4"
                onSubmit={(event) => {
                    event.preventDefault();
                    submit();
                }}>
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="grid gap-x-4 sm:grid-cols-2">
                        <SelectField
                            control={control}
                            errors={errors}
                            name="account_id"
                            label="Brand"
                            placeholder="Pick a brand"
                            options={brandOptions}
                            required />
                        {/* What the chosen brand contributes to the body, shown
                            before the call goes out so a login is never filed
                            against the wrong brand. */}
                        <div className="mb-3 rounded-lg border border-gray-200 bg-light/60 p-3 text-xs">
                            {selectedBrand ? (
                                <div className="space-y-1">
                                    <div className="flex gap-2">
                                        <span className="w-28 shrink-0 text-muted">account_ext_id</span>
                                        <code className="truncate text-gray-700">{selectedBrand.ext_id || "—"}</code>
                                    </div>
                                    <div className="flex gap-2">
                                        <span className="w-28 shrink-0 text-muted">api_key</span>
                                        <code className="truncate text-gray-700">{selectedBrand.api_key || "—"}</code>
                                    </div>
                                </div>
                            ) : (
                                <span className="text-muted">Pick a brand to see the api_key and external id that will be sent.</span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                    <HolderSideFields
                        prefix="admin"
                        title="Brand admin"
                        subtitle="Signs into the brand workspace."
                        methods={methods}
                        enabled={createAdmin} />
                    <HolderSideFields
                        prefix="affiliate"
                        title="Publisher"
                        subtitle="holder_type affiliate — signs into the publisher workspace."
                        methods={methods}
                        enabled={createAffiliate} />
                </div>

                <div className="flex items-center justify-end">
                    <LoadingButton
                        type="submit"
                        text="Create logins"
                        loading={loading}
                        loadingText="Creating…"
                        disabled={!createAdmin && !createAffiliate} />
                </div>
            </form>

            <CreatedCredentials rows={created} title="Logins created this session" onDismiss={dismissCreated} />
        </div>
    );
};

export default HoldersPanel;
